import { InventoryItem, ServiceOrder } from "@turizi/shared";
import { useMemo, useState } from "react";
import { calculateOccupiedSlots } from '@/lib/models-utils';
import { useTranslation } from "react-i18next";
import {
    useInventoryItems,
    useAddServiceOrderInventoryItem,
    useRemoveServiceOrderInventoryItem
} from "@/hooks/api-hooks";
import { Loader, Popover, Badge, ScrollArea, TextInput, Button, Modal } from "@mantine/core";
import {
    IconSearch,
    IconUsersGroup,
    IconTrash,
    IconBusFilled,
    IconSofa,
    IconChairDirector,
    IconArmchair,
    IconCube,
    IconBus,
    IconCar,
    IconSailboat,
    IconPlane,
    IconHelicopter,
    IconBike,
    IconBuilding,
    IconBed,
    IconHome,
    IconCompass,
    IconLuggage,
    IconTools,
    IconUser,
    IconUsers,
    IconBabyCarriage,
    IconWheelchair,
    IconBriefcase,
} from "@tabler/icons-react";
import { notify } from "@/lib/notify";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
    IconBus, IconCar, IconSailboat, IconPlane, IconHelicopter, IconBike,
    IconBuilding, IconBed, IconHome, IconCompass, IconCube, IconLuggage,
    IconTools, IconArmchair, IconUser, IconUsers, IconUsersGroup,
    IconBabyCarriage, IconWheelchair, IconBriefcase
};

const getSelfAttrValue = (attrs: any[] | undefined, key: string, defaultValue: string) => {
    if (!attrs) return defaultValue;
    const attr = attrs.find(a => a.key === key);
    return attr ? attr.value : defaultValue;
};

const getItemIcon = (item: InventoryItem) => {
    const iconName = getSelfAttrValue(item.self_attrs, 'item_icon', 'IconCube');
    return ICON_MAP[iconName] || IconCube;
};

export const getCapacityIcon = (item: InventoryItem) => {
    const iconName = getSelfAttrValue(item.self_attrs, 'capacity_icon', 'IconArmchair');
    return ICON_MAP[iconName] || IconArmchair;
};

interface Props {
    order: ServiceOrder;
    renderButtonOnly?: boolean;
    opened?: boolean;
    onClose?: () => void;
}

export default function ServiceOrderInventorySelection({
    order,
    renderButtonOnly = false,
    opened: propOpened,
    onClose
}: Props) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [search, setSearch] = useState('');
    const [updatingInventory, setUpdatingInventory] = useState(false);
    const [loadingItemIds, setLoadingItemIds] = useState<number[]>([]);

    const { data: inventoryItemsRes } = useInventoryItems();
    const inventoryItems = inventoryItemsRes?.data || [];

    const addLink = useAddServiceOrderInventoryItem();
    const removeLink = useRemoveServiceOrderInventoryItem();

    const selectedLinks = useMemo(() => {
        return order.service_order_inventory_items || [];
    }, [order]);

    const linkedInventoryIds = useMemo(() => {
        return selectedLinks.map((link: any) => link.inventory_item_id);
    }, [selectedLinks]);

    const handleSelect = async (item: InventoryItem) => {
        setUpdatingInventory(true);
        try {
            await addLink.mutateAsync({
                serviceOrderId: order.id,
                inventoryItemId: item.id
            });
            if (onClose) {
                onClose();
            } else {
                setOpened(false);
            }
            setSearch('');
        } catch (error) {
            notify.error(t('error'), t('inventory_update_failed'));
        } finally {
            setUpdatingInventory(false);
        }
    };

    const handleRemove = async (linkId: number) => {
        setLoadingItemIds(prev => [...prev, linkId]);
        try {
            await removeLink.mutateAsync({
                serviceOrderId: order.id,
                linkId: linkId
            });
        } catch (error) {
            console.log(error);
            notify.error(t('error'), t('inventory_update_failed'));
        } finally {
            setLoadingItemIds(prev => prev.filter(id => id !== linkId));
        }
    };

    const filteredItems = inventoryItems.filter((item) => {
        const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
            item.full_path?.toLowerCase().includes(search.toLowerCase());
        const isAlreadySelected = linkedInventoryIds.includes(item.id);
        return matchesSearch && !isAlreadySelected;
    });

    const popoverContent = (
        <Popover
            opened={opened && !updatingInventory}
            onChange={setOpened}
            width="340"
            position="bottom-end"
            shadow="md"
            offset={{ mainAxis: 5 }}
        >
            <Popover.Target>
                <Button
                    onClick={() => setOpened((o) => !o)}
                    loading={updatingInventory}
                    size="xs"
                    variant="outline"
                    color="black"
                    radius="xl"
                    leftSection={<IconBusFilled size={14} />}
                    className="h-8 text-xs font-semibold"
                >
                    {t('assign_inventory')}
                </Button>
            </Popover.Target>

            <Popover.Dropdown className="p-0 border-gray-200">
                <div className="p-2 border-b border-gray-100">
                    <TextInput
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                        placeholder={t('search_inventory')}
                        leftSection={<IconSearch size={14} />}
                        size="xs"
                    />
                </div>
                <ScrollArea.Autosize type="scroll" mah={300}>
                    <div>
                        {filteredItems.length > 0 ? (
                            filteredItems.map((item) => (
                                <div
                                    key={item.id}
                                    className="px-2 py-1 mb-0.5 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => handleSelect(item)}
                                >
                                    <span className="text-xxs text-gray-500 truncate block">
                                        {item.full_path}
                                    </span>
                                    <div className="flex justify-between items-center w-full">
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            {(() => {
                                                const ItemIcon = getItemIcon(item);
                                                return <ItemIcon size={14} className="text-slate-500 shrink-0" />;
                                            })()}
                                            <span className="text-sm truncate">
                                                {item.name}
                                            </span>
                                        </div>
                                        <Badge size="xs" variant="light" color="gray" className="shrink-0">
                                            <div className="flex items-center gap-1">
                                                {(() => {
                                                    const CapacityIcon = getCapacityIcon(item);
                                                    return <CapacityIcon size={10} />;
                                                })()}
                                                {item.capacity || 0}
                                            </div>
                                        </Badge>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-xs text-gray-500">
                                {t('no_items_found')}
                            </div>
                        )}
                    </div>
                </ScrollArea.Autosize>
            </Popover.Dropdown>
        </Popover>
    );

    if (propOpened !== undefined && onClose !== undefined) {
        return (
            <Modal
                opened={propOpened}
                onClose={onClose}
                title={t('assign_inventory')}
                size="sm"
                centered
            >
                <div className="space-y-4">
                    <TextInput
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                        placeholder={t('search_inventory')}
                        leftSection={<IconSearch size={14} />}
                        size="sm"
                    />
                    <ScrollArea.Autosize type="scroll" mah={300}>
                        <div className="space-y-1">
                            {filteredItems.length > 0 ? (
                                filteredItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className="p-2 hover:bg-gray-100 cursor-pointer rounded-lg transition-colors"
                                        onClick={() => handleSelect(item)}
                                    >
                                        <span className="text-xxs text-gray-500 truncate block">
                                            {item.full_path}
                                        </span>
                                        <div className="flex justify-between items-center w-full">
                                            <div className="flex items-center gap-2 min-w-0">
                                                {(() => {
                                                    const ItemIcon = getItemIcon(item);
                                                    return <ItemIcon size={14} className="text-slate-500 shrink-0" />;
                                                })()}
                                                <span className="text-sm font-semibold truncate">
                                                    {item.name}
                                                </span>
                                            </div>
                                            <Badge size="xs" variant="light" color="gray" className="shrink-0">
                                                <div className="flex items-center gap-1">
                                                    {(() => {
                                                        const CapacityIcon = getCapacityIcon(item);
                                                        return <CapacityIcon size={10} />;
                                                    })()}
                                                    {item.capacity || 0}
                                                </div>
                                            </Badge>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 text-center text-xs text-gray-500">
                                    {t('no_items_found')}
                                </div>
                            )}
                        </div>
                    </ScrollArea.Autosize>
                </div>
            </Modal>
        );
    }

    if (renderButtonOnly) {
        return popoverContent;
    }

    return (
        <div className="flex gap-1 flex-wrap">
            {popoverContent}
            {selectedLinks.map((link: any) => {
                const item = link.inventory_item || inventoryItems.find(i => i.id === link.inventory_item_id);
                if (!item) return null;

                return (
                    <div
                        key={link.id}
                        className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                        <div className="flex items-center gap-2">
                            {(() => {
                                const ItemIcon = getItemIcon(item);
                                return <ItemIcon size={15} className="text-slate-500 shrink-0" />;
                            })()}
                            <div className="flex flex-col">
                                {item.full_path && <span className="text-[9px] text-gray-400 uppercase tracking-widest leading-tight">{item.full_path}</span>}
                                <span className="text-xs font-bold text-gray-800 whitespace-nowrap">{item.name}</span>
                            </div>
                        </div>
                        <Button
                            onClick={() => handleRemove(link.id)}
                            size="compact-xs"
                            loading={loadingItemIds.includes(link.id)}
                            variant="subtle"
                            color="gray"
                        >
                            <IconTrash size={14} />
                        </Button>
                    </div>
                );
            })}
        </div>
    );
}

export function ServiceOrderInventoryRows({ order, readOnly = false }: { order: ServiceOrder; readOnly?: boolean }) {
    const { t } = useTranslation();
    const [loadingItemIds, setLoadingItemIds] = useState<number[]>([]);
    const removeLink = useRemoveServiceOrderInventoryItem();
    const { data: inventoryItemsRes } = useInventoryItems();
    const inventoryItems = inventoryItemsRes?.data || [];

    const selectedLinks = order.service_order_inventory_items || [];

    const totalOccupiedSlots = calculateOccupiedSlots(order);

    const handleRemove = async (linkId: number) => {
        setLoadingItemIds(prev => [...prev, linkId]);
        try {
            await removeLink.mutateAsync({
                serviceOrderId: order.id,
                linkId: linkId
            });
        } catch (error) {
            console.log(error);
            notify.error(t('error'), t('inventory_update_failed'));
        } finally {
            setLoadingItemIds(prev => prev.filter(id => id !== linkId));
        }
    };

    if (selectedLinks.length === 0) return null;

    const getIconColor = (occupied: number, capacity: number) => {
        if (occupied < capacity) return 'text-green-500';
        if (occupied > capacity) return 'text-red-500';
        return 'text-gray-500';
    };

    return (
        <>
            {selectedLinks.map((link: any) => {
                const item = link.inventory_item || inventoryItems.find(i => i.id === link.inventory_item_id);
                if (!item) return null;
                return (
                    <tr key={link.id} className="hover:bg-gray-50/50 transition-colors">
                        <td>
                            <div className="flex items-center gap-2 px-2">
                                {(() => {
                                    const ItemIcon = getItemIcon(item);
                                    return <ItemIcon size={16} stroke={1} className="text-slate-500 shrink-0" />;
                                })()}
                                <div className="flex flex-col">
                                    <span className="text-xs font-bold text-gray-800">{item.name}</span>
                                </div>
                            </div>
                        </td>

                        <td>
                            <div className="flex items-center gap-1.5 text-xs text-gray-800 font-medium">
                                {(() => {
                                    const CapacityIcon = getCapacityIcon(item);
                                    return <CapacityIcon size={14} stroke={1.5} className={`${getIconColor(totalOccupiedSlots, item.capacity)} shrink-0`} />;
                                })()}
                                <span className="font-bold">{totalOccupiedSlots}</span> / {item.capacity || 0}
                            </div>
                        </td>
                        <td>
                            <span className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">
                                {item.full_path}
                            </span>
                        </td>
                        {!readOnly && (
                            <td className="py-2.5 px-4 text-right">
                                <Button
                                    onClick={() => handleRemove(link.id)}
                                    loading={loadingItemIds.includes(link.id)}
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                >
                                    <IconTrash size={14} stroke={1.5} className="text-gray-300" />
                                </Button>
                            </td>
                        )}
                    </tr>
                );
            })}
        </>
    );
}
